export function normalizeSearchText(value) {
  return String(value || '')
    .replace(/&/g, ' and ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Match a search query against listing text fields.
 * Supports full-phrase match and multi-token (all words) match.
 *
 * @param {string} query
 * @param {Array<string|number|null|undefined>} parts
 */
export function matchesListingSearchQuery(query, parts) {
  const q = normalizeSearchText(query);
  if (!q) return true;

  const haystack = parts
    .filter((part) => part != null && String(part).trim())
    .map((part) => String(part))
    .join(' ');

  const normalizedHaystack = normalizeSearchText(haystack);
  if (!normalizedHaystack) return false;
  if (normalizedHaystack.includes(q)) return true;

  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.length > 0 && tokens.every((token) => normalizedHaystack.includes(token));
}

/**
 * Find entity IDs whose display names match the query.
 *
 * @param {string} query
 * @param {Array<Record<string, unknown>>} entries
 * @param {{ idKey?: string, nameKeys?: string[] }} [options]
 */
export function findMatchingIdsByName(query, entries, options = {}) {
  const { idKey = 'id', nameKeys = ['name', 'label'] } = options;
  const q = normalizeSearchText(query);
  if (!q) return new Set();

  const ids = new Set();
  for (const entry of entries || []) {
    const id = entry?.[idKey] ?? entry?.id;
    if (id == null) continue;

    const matched = nameKeys.some((key) => {
      const name = normalizeSearchText(entry?.[key]);
      return name && name.includes(q);
    });

    if (matched) ids.add(String(id));
  }

  return ids;
}

/**
 * @param {string} query
 * @param {Array<{ id: string|number, name: string }>} categories
 */
export function findMatchingCategoryIds(query, categories) {
  return findMatchingIdsByName(query, categories, {
    idKey: 'id',
    nameKeys: ['name', 'label', 'category_name', 'category'],
  });
}

/**
 * @param {string} query
 * @param {Array<{ id: string|number, name: string }>} subCategories
 */
export function findMatchingSubCategoryIds(query, subCategories) {
  return findMatchingIdsByName(query, subCategories, {
    idKey: 'id',
    nameKeys: ['name', 'label', 'sub_category_name', 'sub_category'],
  });
}

/**
 * @param {string} query
 * @param {Array<{ id: string|number, name: string }>} subSubCategories
 */
export function findMatchingSubSubCategoryIds(query, subSubCategories) {
  return findMatchingIdsByName(query, subSubCategories, {
    idKey: 'id',
    nameKeys: ['name', 'label', 'sub_sub_category_name', 'sub_sub_category', 'facility_sub_sub_type'],
  });
}
